import { Match, Switch, type JSX } from "solid-js";
import { describeError } from "../api/result.ts";
import { useI18n } from "../i18n/provider.tsx";
import { Button } from "./Form.tsx";
import type { Loaded } from "../lib/async.ts";
import styles from "./Async.module.scss";

export type AsyncProps<T> = {
  value: Loaded<T>;
  onRetry?: () => void;
  /**
   * Receives the value as of the moment the state became loaded, not a live
   * accessor: `Match` calls this untracked. A child that edits and then reads
   * back through `Loader.put` must read the loader itself for the fresh value.
   */
  children: (value: T) => JSX.Element;
};

/** The one place loading and failure are rendered, so no screen invents its own. */
export const Async = <T,>(props: AsyncProps<T>) => {
  const i18n = useI18n();
  const failure = () => (props.value.state === "failed" ? props.value.error : undefined);
  const loaded = () => (props.value.state === "loaded" ? props.value : undefined);

  return (
    <Switch>
      <Match when={props.value.state === "loading"}>
        <p class={styles.note}>{i18n.t().common.loading}</p>
      </Match>
      <Match when={failure()}>
        {(error) => (
          <div class={styles.failure} role="alert">
            <p>{describeError(i18n.t(), error())}</p>
            {props.onRetry === undefined ? null : (
              <Button label={i18n.t().common.retry} onClick={props.onRetry} />
            )}
          </div>
        )}
      </Match>
      <Match when={loaded()}>{(value) => props.children(value().value)}</Match>
    </Switch>
  );
};
